import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Phone,
  MapPin,
  Calendar,
  Search,
  Eye,
  X,
  User,
  CreditCard,
  ArrowRight,
  RefreshCw,
} from "lucide-react";

const ESTADOS = [
  { value: "pendiente", label: "Pendiente", color: "amber", icon: Clock },
  { value: "pagado_pendiente_entrega", label: "Pagado - Pend. Entrega", color: "blue", icon: CreditCard },
  { value: "confirmado", label: "Confirmado", color: "indigo", icon: CheckCircle },
  { value: "en_transito", label: "En Tránsito", color: "purple", icon: Truck },
  { value: "entregado", label: "Entregado", color: "green", icon: CheckCircle },
  { value: "cancelado", label: "Cancelado", color: "red", icon: XCircle },
];

export default function AdminPedidosTerceros() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingEstado, setUpdatingEstado] = useState(false);

  useEffect(() => {
    fetchPedidos();
  }, [filtroEstado]);

  const fetchPedidos = async () => {
    try {
      const params = filtroEstado ? `?estado=${filtroEstado}` : "";
      const res = await api.get(`/pedidos-terceros${params}`);
      setPedidos(res.data);
    } catch (error) {
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  };

  const updateEstado = async (pedidoId, nuevoEstado) => {
    setUpdatingEstado(true);
    try {
      await api.put(`/pedidos-terceros/${pedidoId}/estado`, { estado: nuevoEstado });
      toast.success(`Estado actualizado a "${ESTADOS.find(e => e.value === nuevoEstado)?.label}"`);
      fetchPedidos();
      if (selectedPedido?.id === pedidoId) {
        const updated = await api.get(`/pedidos-terceros/${pedidoId}`);
        setSelectedPedido(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar estado");
    } finally {
      setUpdatingEstado(false);
    }
  };

  const getEstadoInfo = (estado) => {
    return ESTADOS.find(e => e.value === estado) || ESTADOS[0];
  };

  const getNextEstados = (estadoActual) => {
    const flujo = {
      "pendiente": ["confirmado", "cancelado"],
      "pagado_pendiente_entrega": ["confirmado", "cancelado"],
      "confirmado": ["en_transito", "cancelado"],
      "en_transito": ["entregado", "cancelado"],
      "entregado": [],
      "cancelado": [],
    };
    return flujo[estadoActual] || [];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  // Calculate stats
  const stats = {
    total: pedidos.length,
    pendientes: pedidos.filter(p => p.estado === "pendiente" || p.estado === "pagado_pendiente_entrega").length,
    enProceso: pedidos.filter(p => p.estado === "confirmado" || p.estado === "en_transito").length,
    entregados: pedidos.filter(p => p.estado === "entregado").length,
    cancelados: pedidos.filter(p => p.estado === "cancelado").length,
  };

  const filteredPedidos = pedidos.filter(
    (p) =>
      p.cliente_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.vendedor_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.cliente_telefono?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pedidos-terceros">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Pedidos a Terceros</h1>
          <p className="text-torta-muted">Gestiona los pedidos de vendedores digitales y a domicilio</p>
        </div>
        <Button onClick={fetchPedidos} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-torta-dark">{stats.total}</p>
              <p className="text-xs text-torta-muted">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-amber-600">{stats.pendientes}</p>
              <p className="text-xs text-torta-muted">Por Confirmar</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-purple-600">{stats.enProceso}</p>
              <p className="text-xs text-torta-muted">En Proceso</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-green-600">{stats.entregados}</p>
              <p className="text-xs text-torta-muted">Entregados</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-display text-red-600">{stats.cancelados}</p>
              <p className="text-xs text-torta-muted">Cancelados</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFiltroEstado("")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              filtroEstado === "" ? "bg-torta-brown text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Todos
          </button>
          {ESTADOS.map((estado) => (
            <button
              key={estado.value}
              onClick={() => setFiltroEstado(estado.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                filtroEstado === estado.value
                  ? `bg-${estado.color}-500 text-white`
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              style={filtroEstado === estado.value ? { backgroundColor: getColorHex(estado.color) } : {}}
            >
              <estado.icon className="w-3 h-3" />
              {estado.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar cliente, vendedor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Pedidos List */}
      <div className="space-y-3">
        {filteredPedidos.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-torta-muted">No hay pedidos {filtroEstado ? `con estado "${getEstadoInfo(filtroEstado).label}"` : ""}</p>
          </div>
        ) : (
          filteredPedidos.map((pedido) => {
            const estadoInfo = getEstadoInfo(pedido.estado);
            const nextEstados = getNextEstados(pedido.estado);
            
            return (
              <div
                key={pedido.id}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Info Principal */}
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-${estadoInfo.color}-100`}
                      style={{ backgroundColor: getColorHex(estadoInfo.color, true) }}
                    >
                      <estadoInfo.icon className={`w-6 h-6 text-${estadoInfo.color}-600`} 
                        style={{ color: getColorHex(estadoInfo.color) }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-medium text-torta-dark">{pedido.cliente_nombre}</h3>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium`}
                          style={{ 
                            backgroundColor: getColorHex(estadoInfo.color, true),
                            color: getColorHex(estadoInfo.color)
                          }}
                        >
                          {estadoInfo.label}
                        </span>
                        {pedido.tipo_recaudo === "pago_vendedor" && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            Pagado
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-torta-muted space-y-0.5">
                        <p className="flex items-center gap-2">
                          <User className="w-3 h-3" /> Vendedor: {pedido.vendedor_nombre}
                        </p>
                        <p className="flex items-center gap-2">
                          <Phone className="w-3 h-3" /> {pedido.cliente_telefono}
                        </p>
                        <p className="flex items-center gap-2 truncate">
                          <MapPin className="w-3 h-3 flex-shrink-0" /> {pedido.cliente_direccion}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Info Entrega y Total */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 lg:gap-6">
                    <div className="text-sm">
                      <p className="text-torta-muted flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Entrega: {pedido.fecha_entrega} - {pedido.hora_entrega}
                      </p>
                      <p className="font-display text-lg text-torta-brown mt-1">
                        {formatCurrency(pedido.total)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {pedido.total_unidades} unidades
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedPedido(pedido);
                          setShowDetailModal(true);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      {nextEstados.length > 0 && (
                        <div className="flex gap-1">
                          {nextEstados.map((estado) => {
                            const info = getEstadoInfo(estado);
                            return (
                              <Button
                                key={estado}
                                size="sm"
                                onClick={() => updateEstado(pedido.id, estado)}
                                disabled={updatingEstado}
                                className={estado === "cancelado" 
                                  ? "bg-red-600 hover:bg-red-700" 
                                  : "bg-torta-brown hover:bg-torta-brown/90"
                                }
                              >
                                <info.icon className="w-4 h-4 mr-1" />
                                {info.label}
                              </Button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedPedido && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-display text-xl text-torta-dark">Detalle del Pedido</h2>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Estado actual */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {(() => {
                    const info = getEstadoInfo(selectedPedido.estado);
                    return (
                      <>
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: getColorHex(info.color, true) }}
                        >
                          <info.icon className="w-6 h-6" style={{ color: getColorHex(info.color) }} />
                        </div>
                        <div>
                          <p className="font-medium text-torta-dark">{info.label}</p>
                          <p className="text-sm text-torta-muted">Estado actual</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl text-torta-brown">{formatCurrency(selectedPedido.total)}</p>
                  <p className="text-sm text-torta-muted">
                    Subtotal: {formatCurrency(selectedPedido.subtotal)} + Domicilio: {formatCurrency(selectedPedido.costo_domicilio)}
                  </p>
                </div>
              </div>

              {/* Cliente Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-medium text-torta-dark mb-3">Información del Cliente</h3>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <p><strong>Nombre:</strong> {selectedPedido.cliente_nombre}</p>
                  <p><strong>Teléfono:</strong> {selectedPedido.cliente_telefono}</p>
                  <p className="sm:col-span-2"><strong>Dirección:</strong> {selectedPedido.cliente_direccion}</p>
                  {selectedPedido.cliente_barrio && (
                    <p><strong>Barrio:</strong> {selectedPedido.cliente_barrio}</p>
                  )}
                </div>
              </div>

              {/* Entrega Info */}
              <div className="bg-blue-50 rounded-xl p-4">
                <h3 className="font-medium text-blue-800 mb-3">Información de Entrega</h3>
                <div className="grid sm:grid-cols-2 gap-3 text-sm text-blue-700">
                  <p><strong>Fecha:</strong> {selectedPedido.fecha_entrega}</p>
                  <p><strong>Hora:</strong> {selectedPedido.hora_entrega}</p>
                  <p><strong>Tipo Recaudo:</strong> {selectedPedido.tipo_recaudo === "pago_vendedor" ? "Pagado al vendedor" : "Contra entrega"}</p>
                  <p><strong>Método Pago:</strong> {selectedPedido.metodo_pago}</p>
                </div>
              </div>

              {/* Vendedor Info */}
              <div className="bg-amber-50 rounded-xl p-4">
                <h3 className="font-medium text-amber-800 mb-3">Vendedor</h3>
                <p className="text-sm text-amber-700">
                  <strong>{selectedPedido.vendedor_nombre}</strong>
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Creado: {formatDate(selectedPedido.fecha)}
                </p>
              </div>

              {/* Items */}
              <div>
                <h3 className="font-medium text-torta-dark mb-3">Productos ({selectedPedido.total_unidades} unidades)</h3>
                <div className="space-y-2">
                  {selectedPedido.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-torta-cream rounded-lg p-3">
                      <div>
                        <p className="font-medium text-torta-dark">{item.cantidad}x {item.producto_nombre}</p>
                        <p className="text-sm text-torta-muted">{formatCurrency(item.precio_unitario)} c/u</p>
                      </div>
                      <p className="font-display text-torta-brown">{formatCurrency(item.subtotal)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notas */}
              {selectedPedido.notas && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-medium text-torta-dark mb-2">Notas</h3>
                  <p className="text-sm text-torta-muted">{selectedPedido.notas}</p>
                </div>
              )}

              {/* Historial de Estados */}
              {selectedPedido.historial_estados && (
                <div>
                  <h3 className="font-medium text-torta-dark mb-3">Historial de Estados</h3>
                  <div className="space-y-2">
                    {selectedPedido.historial_estados.map((h, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-torta-brown" />
                        <span className="font-medium">{getEstadoInfo(h.estado).label}</span>
                        <span className="text-torta-muted">- {h.usuario}</span>
                        <span className="text-gray-400">{formatDate(h.fecha)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {getNextEstados(selectedPedido.estado).length > 0 && (
                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  {getNextEstados(selectedPedido.estado).map((estado) => {
                    const info = getEstadoInfo(estado);
                    return (
                      <Button
                        key={estado}
                        onClick={() => updateEstado(selectedPedido.id, estado)}
                        disabled={updatingEstado}
                        className={`flex-1 ${estado === "cancelado" 
                          ? "bg-red-600 hover:bg-red-700" 
                          : "bg-torta-brown hover:bg-torta-brown/90"
                        }`}
                      >
                        <info.icon className="w-4 h-4 mr-2" />
                        Cambiar a {info.label}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to get color hex values
function getColorHex(color, light = false) {
  const colors = {
    amber: light ? "#fef3c7" : "#d97706",
    blue: light ? "#dbeafe" : "#2563eb",
    indigo: light ? "#e0e7ff" : "#4f46e5",
    purple: light ? "#f3e8ff" : "#9333ea",
    green: light ? "#dcfce7" : "#16a34a",
    red: light ? "#fee2e2" : "#dc2626",
    gray: light ? "#f3f4f6" : "#6b7280",
  };
  return colors[color] || colors.gray;
}
